import { cn } from "@gymflow/helpers";
import {
  ClubOnboardingSettingsDTO,
  UserMemberProfileDTO,
} from "@gymflow/types";
import { FormikProvider, useFormik } from "formik";
import { AnimatePresence } from "framer-motion";
import { useMutationProfile } from "libs/api/src/lib/hooks/memberAsMember/useMutationProfile";
import { useMutationProfilePicture } from "libs/api/src/lib/hooks/memberAsMember/useMutationProfilePicture";
import { omit } from "lodash";
import { usePostHog } from "posthog-js/react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { OnboardingSchemaHelper } from "../../../../../libs/validation/src/OnboardingSchema";
import { Button } from "../../components/atoms";
import useGymflowModels from "../../store";
import { ModalContext } from "../ModalProvider";
import { useClubSettings } from "../settings";
import { OnboardingCompleteModal } from "./OnboardingCompleteModal";
import { OnboardingFormStep1PersonalDetails } from "./OnboardingFormStep1PersonalDetails";
import { OnboardingFormStep2Address } from "./OnboardingFormStep2Address";
import { OnboardingFormStep3EmergencyContact } from "./OnboardingFormStep3EmergencyContact";
import { OnboardingFormStep4WaiverDetails } from "./OnboardingFormStep4WaiverDetails";

export const OnboardingForm = ({
  onboardingSettings,
  profile,
  setCurrentScreen,
  currentScreen,
}: {
  onboardingSettings: ClubOnboardingSettingsDTO;
  profile: UserMemberProfileDTO;
  setCurrentScreen: (currentScreen: "none" | "banner" | "form") => void;
  currentScreen: "none" | "banner" | "form";
}) => {
  const { t } = useTranslation();
  const { properties } = useClubSettings();
  const { api } = useGymflowModels();
  const { stackModal, popModal } = useContext(ModalContext);
  const { mutateAsync: updateProfile } = useMutationProfile({
    api,
  });
  const { mutateAsync: updateProfilePicture } = useMutationProfilePicture({
    api,
  });
  const steps = useMemo(
    () =>
      OnboardingSchemaHelper.getSteps(onboardingSettings).filter(
        (step) => !profile.clubWaiverAccepted || step !== "waiverDetails",
      ),
    [onboardingSettings, profile.clubWaiverAccepted],
  );
  const [currentStep, setCurrentStep] = useState<(typeof steps)[number]>(
    steps[0],
  );
  const currentStepIndex = steps.indexOf(currentStep);
  const schemas = useMemo(
    () =>
      OnboardingSchemaHelper.generator(
        onboardingSettings,
        properties.postalCodeCountry,
        profile?.mobileNumber || "",
      ),
    [onboardingSettings, profile?.mobileNumber, properties.postalCodeCountry],
  );
  const initialValues = useMemo(
    () => ({
      ...OnboardingSchemaHelper.defaultValues,
      ...profile,
    }),
    [profile],
  );

  const stepComponents = {
    personalDetails: (
      <OnboardingFormStep1PersonalDetails
        onboardingSettings={onboardingSettings}
      />
    ),
    addressDetails: (
      <OnboardingFormStep2Address onboardingSettings={onboardingSettings} />
    ),
    emergencyContactDetails: (
      <OnboardingFormStep3EmergencyContact
        onboardingSettings={onboardingSettings}
      />
    ),
    waiverDetails: <OnboardingFormStep4WaiverDetails />,
  };
  const posthog = usePostHog();
  const formik = useFormik({
    initialValues,
    validationSchema: toFormikValidationSchema(schemas[currentStep]),
    enableReinitialize: true,
    onSubmit: async (values, actions) => {
      const currentStepValues = schemas[currentStep].parse(values);
      const toSubmit = omit(currentStepValues, "picture");
      await updateProfile(toSubmit as any);
      if (
        currentStepValues.picture &&
        currentStepValues.picture !== profile.picture
      ) {
        await updateProfilePicture(currentStepValues.picture as string);
      }
      if (currentStepIndex < steps.length - 1) {
        setCurrentStep(steps[currentStepIndex + 1]);
        actions.setTouched({});
        actions.setSubmitting(false);
      } else {
        posthog?.capture("onboarding_completed");
        setCurrentScreen("none");
        stackModal(
          <OnboardingCompleteModal
            onClose={() => {
              popModal();
            }}
          />,
        );
      }
    },
  });

  useEffect(() => {
    posthog?.capture("onboarding_started", {
      stepsTotal: steps.length,
      onboardingSettings,
    });
  }, [posthog, steps.length, onboardingSettings]);

  return (
    <FormikProvider value={formik}>
      <div
        className={cn(
          "bg-gray-0 flex size-full flex-col items-center justify-between overflow-y-auto dark:bg-gray-800",
          {
            "track-height-overlay": currentScreen === "form",
          },
        )}
      >
        <div className="flex size-full min-h-fit justify-center gap-4 px-4 py-6">
          <AnimatePresence>{stepComponents[currentStep]}</AnimatePresence>
        </div>
        <div className="min-h-fit w-full max-w-lg flex-col gap-4 p-4">
          <div className="flex flex-row gap-4 px-24">
            {steps.map((step: keyof typeof stepComponents, index: number) => (
              <div
                key={step}
                className={cn(
                  "h-1 flex-1 rounded-full bg-gray-200 transition-all",
                  index <= currentStepIndex && "bg-secondary-500",
                )}
              />
            ))}
          </div>
          <div className="mt-4 flex w-full flex-row gap-2">
            {currentStepIndex > 0 && (
              <Button
                onClick={() => setCurrentStep(steps[currentStepIndex - 1])}
                className="flex-1"
              >
                {t("portal.common.previous")}
              </Button>
            )}
            <Button
              intent="secondary"
              className="flex-1"
              onClick={async () => await formik.submitForm()}
            >
              {currentStepIndex === steps.length - 1
                ? t("portal.common.complete")
                : t("portal.common.next")}
            </Button>
          </div>
        </div>
      </div>
    </FormikProvider>
  );
};
